import type { PaywallPayload } from "./billing.types";

/**
 * Shared helpers for the entitlement/paywall workflow so every AI generation
 * entry point (Test Cases, Requirements, future features) treats a 402 the
 * same way instead of each component re-deriving its own detection/dispatch
 * logic.
 *
 * `PaywallModal` (mounted once at the app root) listens for the same
 * `paywall:open` window event the axios interceptor dispatches on a real
 * HTTP 402 — `openPaywallModal` lets a *frontend* pre-check (before any
 * network call is made) show the exact same modal for an already-known
 * entitlement rejection.
 */

/** True when an axios error is the backend's upgrade-required contract. */
export function isPaywallError(err: unknown): boolean {
  const status = (err as any)?.response?.status;
  const data = (err as any)?.response?.data;
  return status === 402 && data?.error === "upgrade_required";
}

/** Extracts the paywall payload from an axios error, if present. */
export function getPaywallPayload(err: unknown): PaywallPayload | null {
  if (!isPaywallError(err)) return null;
  return (err as any).response.data as PaywallPayload;
}

/**
 * Opens the global upgrade modal without making a network request — used by
 * frontend entitlement pre-checks (e.g. "Generate with AI" clicked while
 * `aiEnabled === false`) so a locked action never has to round-trip to the
 * backend just to discover it's locked.
 */
export function openPaywallModal(payload: PaywallPayload): void {
  window.dispatchEvent(new CustomEvent("paywall:open", { detail: payload }));
}

/** Standard payload for the shared "ai" feature gate. */
export const AI_PAYWALL_PAYLOAD: PaywallPayload = {
  error: "upgrade_required",
  feature: "ai",
  upgrade_url: "/pricing",
};
